/**
 * Shared client-side validation for the pay-to address (X12 837P 2010AB).
 *
 * The setup wizard and the Edit Billing Identity modal collect the same four
 * required fields under the same rules, so the rules live here rather than
 * being duplicated (and drifting) per surface. Error keys are namespaced per
 * field so each input surfaces its own message.
 */

/** Structural shape of the pay-to fields; matches BillingEntityAddressFormData. */
export interface PayToAddressFields {
  line1: string;
  city: string;
  state: string;
  zip: string;
}

export const PAY_TO_ADDRESS_ERROR_KEYS = [
  'pay_to_address.line1',
  'pay_to_address.city',
  'pay_to_address.state',
  'pay_to_address.zip',
] as const;

/**
 * Validate an opted-in pay-to address.
 *
 * Only call this when the provider has enabled a separate pay-to address —
 * an untouched, disabled section is not an error.
 *
 * @returns Per-field errors keyed `pay_to_address.<field>`; empty when valid.
 */
export function validatePayToAddress(address: PayToAddressFields): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!address.line1.trim()) {
    errors['pay_to_address.line1'] = 'Pay-to address line 1 is required';
  }
  if (!address.city.trim()) {
    errors['pay_to_address.city'] = 'Pay-to city is required';
  }
  if (address.state.trim().length !== 2) {
    errors['pay_to_address.state'] = 'Valid 2-letter state code is required';
  }
  if (!/^\d{5}(-\d{4})?$/.test(address.zip.trim())) {
    errors['pay_to_address.zip'] = 'Valid ZIP code is required (XXXXX or XXXXX-XXXX)';
  }

  return errors;
}
